import axios from "axios";

/*
 * Live-call APIs for the CallWidget. The backend resolves the logged-in
 * user's Call Engine agent and caller ID itself, so no agent IDs, tenant keys
 * or Call Engine credentials are ever sent from here.
 */

const authHeaders = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

// Keep the HTTP status next to the backend's { status, message, errors } body,
// so callers can tell e.g. a 409 (call already over) from a real failure.
const toError = (err) => {
  if (axios.isAxiosError(err)) {
    return {
      httpStatus: err.response?.status,
      message: err.message,
      ...(err.response?.data ?? {}),
    };
  }
  return err;
};

const request = async (fn) => {
  try {
    const res = await fn();
    return res.data;
  } catch (err) {
    throw toError(err);
  }
};

/*
 * Softphone. session/ issues a temporary SIP credential and rotates the SIP
 * password on every call, so it is only called from an explicit Connect
 * Phone click - never on page load. Keep its response in memory only.
 */
export const createSoftphoneSession = (token) =>
  request(() =>
    axios.post("dialer/softphone/session/", null, authHeaders(token))
  );

// Fresh TURN credentials; does not rotate the SIP password.
export const getSoftphoneIceServers = (token) =>
  request(() => axios.get("dialer/softphone/ice-servers/", authHeaders(token)));

export const getAgentPresence = (token) =>
  request(() => axios.get("dialer/agent-presence/", authHeaders(token)));

// presence: "available" | "break"
export const setAgentPresence = (presence, token) =>
  request(() =>
    axios.patch("dialer/agent-presence/", { presence }, authHeaders(token))
  );

export const initiateCall = (payload, token) =>
  request(() =>
    axios.post("dialer/calls/initiate/", payload, authHeaders(token))
  );

export const getCallStatus = (callId, token) =>
  request(() => axios.get(`dialer/calls/${callId}/`, authHeaders(token)));

export const hangupCall = (callId, token) =>
  request(() =>
    axios.delete(`dialer/calls/${callId}/hangup/`, authHeaders(token))
  );

export const holdCall = (callId, token) =>
  request(() =>
    axios.post(`dialer/calls/${callId}/hold/`, null, authHeaders(token))
  );

export const resumeCall = (callId, token) =>
  request(() =>
    axios.post(`dialer/calls/${callId}/resume/`, null, authHeaders(token))
  );

export const saveCallDisposition = (callId, disposition, token) =>
  request(() =>
    axios.patch(
      `dialer/calls/${callId}/disposition/`,
      { disposition },
      authHeaders(token)
    )
  );

// payload: { mode: "blind" | "attended", to: "+91..." }
export const transferCall = (callId, payload, token) =>
  request(() =>
    axios.post(`dialer/calls/${callId}/transfer/`, payload, authHeaders(token))
  );

export const completeTransfer = (callId, token) =>
  request(() =>
    axios.post(
      `dialer/calls/${callId}/transfer/complete/`,
      null,
      authHeaders(token)
    )
  );

export const cancelTransfer = (callId, token) =>
  request(() =>
    axios.post(
      `dialer/calls/${callId}/transfer/cancel/`,
      null,
      authHeaders(token)
    )
  );

// People the logged-in telecaller can add to a call:
// [{ user_id, name, role, available }]
export const getConferenceTargets = (token) =>
  request(() =>
    axios.get("dialer/conference-participants/", authHeaders(token))
  );

// payload: { user_id?, mode: "listen" | "speak" | "whisper" }
export const addParticipant = (callId, payload, token) =>
  request(() =>
    axios.post(
      `dialer/calls/${callId}/add-participant/`,
      payload,
      authHeaders(token)
    )
  );

// payload: any of { mode, whisper_target, muted, deafened }
export const updateParticipant = (callId, participantId, payload, token) =>
  request(() =>
    axios.patch(
      `dialer/calls/${callId}/update-participant/${participantId}/`,
      payload,
      authHeaders(token)
    )
  );

export const removeParticipant = (callId, participantId, token) =>
  request(() =>
    axios.delete(
      `dialer/calls/${callId}/remove-participant/${participantId}/`,
      authHeaders(token)
    )
  );

/*
 * Outbound call history of the logged-in telecaller's agent (Django adds the
 * agent and direction). Cursor-paginated: no page=2, no total count.
 * params: { page_size, state, disposition, reference, from_date, to_date }
 */
export const getCallHistory = (params, token) =>
  request(() =>
    axios.get("dialer/calls/history/", { ...authHeaders(token), params })
  );

// Next page from the previous response's data.next. That URL is absolute and
// its host has not always matched the API's (port missing, http), so its
// query - cursor untouched - is replayed against our own base URL.
export const getCallHistoryPage = (nextUrl, token) => {
  const { search } = new URL(nextUrl, window.location.origin);
  return request(() =>
    axios.get(`dialer/calls/history/${search}`, authHeaders(token))
  );
};

/* Telecaller data the widget shows between calls. */

export const getCallbackList = (params, token) =>
  request(() =>
    axios.get("telecaller/callback-list/", { ...authHeaders(token), params })
  );

export const getTelecallerStats = (token) =>
  request(() => axios.get("telecaller/stats/", authHeaders(token)));
